import { CheckBox, Spacer, VDBText } from "@components/atoms";
import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import { Pressable } from "react-native";
import { Image as ExpoImage } from "expo-image";
import { SpaceType } from "@components/atoms/Spacer/types";
import LabelView from "../LabelView";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { useTheme } from "styled-components/native";
import { SearchResultListTypes } from "./types";
import { Diamond } from "@src/types/DiamondDetail";
import currency from "currency.js";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Menu, MenuItem, MenuItemLabel } from "@gluestack-ui/themed";
import { Platform } from "react-native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "../PriceModeCard/types";
import { getTwoProductProperty } from "@utils/product/productProperties";
import { GlobalEnum, ProductCategory } from "@utils/constants";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import Tooltip from "@components/atoms/Tooltip";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { IconState } from "../IconUtils";
import useThreeDotMenuState from "../../../hooks/useThreeDotMenuState";
import { VDBIcons } from "@utils/VDBIcons";
import { getOptimizedListImageUrl, isImageUrlEmptyOrUndefined } from "../Utils";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { getProductThemeFromType } from "@components/common/Header/utils";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { percent } from "@utils/product/productFormatters";
import { productLevelOneColor } from "@utils/product/productTheme";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import { isPair } from "@utils/product/productValidators";

const GemstoneSearchResultListItem = ({
  mItem,
  menuOptions,
  menuOptionSelected,
  checkBoxSelection,
  ismItemChecked,
  handleItemClick,
  favoriteClickedFunction,
  productType,
  listExpanded,
}: SearchResultListTypes) => {
  const userInfo = useUserInfo();
  const { addRouteToHistory } = useNavigationHistory();
  const ImageComponent = ExpoImage;
  const priceMode = userInfo?.user?.price_mode;
  const theme = useTheme();
  const userCurrency = useUserCurrency();
  const productPrefix = useProductPrefix();
  const { deviceWidth } = useIsFoldable();
  const screenWidth = deviceWidth - (theme.isMobile ? 24 : 32);
  const typography = useTypographyStore();
  const [imageUri, setImageUri] = useState(getOptimizedListImageUrl(mItem));
  const [isFavorite, setIsFavorite] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isItemChecked, setIsItemChecked] = useState(false);
  const productTheme = getProductThemeFromType(productType as ProductCategory);
  const [showExpandedListView, setShowExpandedListView] =
    useState(listExpanded);
  const [showImageContainer, setShowImageContainer] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
  );
  const [showErrorImage, setShowErrorImage] = useState(
    isImageUrlEmptyOrUndefined(mItem)
  );
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const checkStateChanged = (checked: boolean) => {
    setIsItemChecked(checked);
    checkBoxSelection(checked, mItem);
  };

  useEffect(() => {
    setIsItemChecked(mItem?.is_selected || ismItemChecked || false);
  }, [ismItemChecked, mItem?.is_selected]);

  useEffect(() => {
    if (mItem?.is_favorite !== undefined) {
      setIsFavorite(mItem?.is_favorite);
    }
  }, [mItem?.is_favorite]);

  const getAvailabilityStatus = () => {
    switch (mItem?.available) {
      case GlobalEnum.OUT_ON_MEMO:
        return "error-01";
      case GlobalEnum.ON_HAND:
        return "success-01";
      case GlobalEnum.PLEASE_EMAIL:
      default:
        return "warn-01";
    }
  };

  const {
    setThreeDotMenuOpen,
    setThreeDotIconState,
    handlePressIn,
    handlePressOut,
    getThreeDotIconBackgroundColor,
  } = useThreeDotMenuState(false);

  return (
    <Pressable
      onPress={() =>
        handleItemClick(
          mItem,
          ProductCategory.GEMSTONE,
          priceMode,
          productPrefix,
          addRouteToHistory
        )
      }
    >
      <ParentContainer theme={theme} isChecked={isItemChecked}>
        <FirstContainer>
          <ImageAndContentContainer>
            <ImageAndCheckboxContainer>
              <CheckBox
                fillColor={theme["knob-bor"]}
                checkBoxSize={20}
                checkedFunction={checkStateChanged}
                isChecked={isItemChecked}
              />
              <ImageResourceContainer>
                {showErrorImage ? (
                  <FallbackImageContainer theme={theme}>
                    <EmptyImagePreview
                      shape={getShapeValue(mItem)}
                      size={30}
                      isPair={isPair(mItem) ?? false}
                    />
                  </FallbackImageContainer>
                ) : showImageContainer ? (
                  <ImageComponent
                    style={{
                      height: 48,
                      width: 48,
                      borderRadius: 8,
                      borderColor: theme["core-03"],
                      borderWidth: 1,
                    }}
                    source={getImageSource(
                      imageUri,
                      ImageSizePreset.LIST_THUMB
                    )}
                    contentFit="cover"
                    recyclingKey={`gem-list-img-${mItem?.id}`}
                    {...getIOSImageProps()}
                    onError={() => {
                      if (mItem?.video_url) {
                        setShowImageContainer(false);
                      } else {
                        setShowErrorImage(true);
                        setShowImageContainer(false);
                        setImageUri(FALLBACK_IMAGE_ASSET);
                      }
                    }}
                  />
                ) : (
                  <VideoBackgroundContainer theme={theme}>
                    <VideoPlayIconContainer theme={theme}>
                      <VDBIcon
                        color={theme["core-06"]}
                        size={20}
                        variant={IconsVariant.VDBCustom}
                        name={VDBIcons.PLAY}
                      />
                    </VideoPlayIconContainer>
                  </VideoBackgroundContainer>
                )}
              </ImageResourceContainer>
              <AvailabilityDot bgColor={theme[getAvailabilityStatus()]} />
            </ImageAndCheckboxContainer>
            <FirstContentContainer>
              {mItem?.short_title && (
                <TitleContainer screenWidth={screenWidth}>
                  <VDBText
                    type={typography["Minor-M"]}
                    color={theme["core-05"]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {mItem?.short_title}
                  </VDBText>
                  <Spacer size={SpaceType.ExtraSmall} />
                </TitleContainer>
              )}
              <Spacer size={SpaceType.ExtraSmall} />
              <LabelView
                itemLeftText="Stock"
                itemRightText={getDiamondValue("Stock", mItem)}
                leftPostfixText="#"
                keyTypographyStyle={typography["Minor-R"]}
                valueTypographyStyle={typography["Minor-M"]}
              />
              <Spacer size={SpaceType.ExtraSmall} />
            </FirstContentContainer>
          </ImageAndContentContainer>

          <OptionsContainer>
            <Pressable
              onPress={() => favoriteClickedFunction(isFavorite, mItem)}
            >
              <VDBIcon
                color={theme[isFavorite ? "error-01" : "core-06"]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={isFavorite ? VDBIcons.FAV : VDBIcons.FAV_EMPTY}
              />
            </Pressable>

            {!isMenuOpen ? (
              <Pressable
                onPressIn={handlePressIn}
                onPressOut={handlePressOut}
                onPress={() => {
                  setIsMenuOpen(true);
                  setThreeDotMenuOpen(true);
                  setThreeDotIconState(IconState.Selected);
                }}
              >
                <VDBIcon
                  color={getThreeDotIconBackgroundColor(theme)}
                  size={24}
                  variant={IconsVariant.VDBCustom}
                  name={VDBIcons.DOTS_MENU_FILLED}
                />
              </Pressable>
            ) : (
              <Menu
                mt={Platform.OS === "ios" ? 0 : -15}
                py={0}
                useRNModal={false}
                marginRight={10}
                placement="top"
                shouldFlip={true}
                isOpen={true}
                onClose={() => {
                  setIsMenuOpen(false);
                  setThreeDotMenuOpen(false);
                  setThreeDotIconState(IconState.Unselected);
                }}
                trigger={({ ...triggerProps }) => (
                  <Pressable
                    onPressIn={handlePressIn}
                    onPressOut={handlePressOut}
                    onPress={() => {
                      setIsMenuOpen(false);
                      setThreeDotMenuOpen(false);
                      setThreeDotIconState(IconState.Unselected);
                    }}
                    {...triggerProps}
                  >
                    <VDBIcon
                      color={getThreeDotIconBackgroundColor(theme)}
                      size={24}
                      variant={IconsVariant.VDBCustom}
                      name={VDBIcons.DOTS_MENU_FILLED}
                    />
                  </Pressable>
                )}
              >
                {menuOptions?.map((option, index) => (
                  <MenuItem
                    key={option.name}
                    textValue={option.label}
                    style={{ padding: 0 }}
                    onPress={() => {
                      setIsMenuOpen(false);
                      setThreeDotMenuOpen(false);
                      setThreeDotIconState(IconState.Unselected);
                      menuOptionSelected(option.name, mItem);
                    }}
                  >
                    <MenuItemContainer>
                      <MenuItemStyle>
                        <MenuItemLabel
                          fontSize={12}
                          fontWeight={"600"}
                          size="xs"
                          color={theme["core-06"]}
                        >
                          {option.label}
                        </MenuItemLabel>
                      </MenuItemStyle>
                      {index < menuOptions.length - 1 && (
                        <LineView theme={theme} />
                      )}
                    </MenuItemContainer>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </OptionsContainer>
        </FirstContainer>
        <SecondContainer
          screenWidth={screenWidth}
          theme={theme}
          priceMode={priceMode}
        >
          <SecondContainerRow screenWidth={screenWidth}>
            <SecondContainerLevelFirst
              screenWidth={screenWidth * 0.5}
              theme={theme}
            >
              <LabelView
                itemLeftText="Ratio"
                itemRightText={getDiamondValue("Ratio", mItem)}
                leftPostfixText=":"
                keyTypographyStyle={typography["Minor-R"]}
                valueTypographyStyle={typography["Minor-M"]}
                cellWidth={screenWidth * 0.24}
                allowSpaceAfterLeftText={false}
              />
            </SecondContainerLevelFirst>

            <SecondContainerLevelTwo screenWidth={screenWidth * 0.4}>
              <LabelView
                itemLeftText="M"
                itemRightText={getDiamondValue("Meas", mItem)}
                leftPostfixText=":"
                keyTypographyStyle={typography["Minor-R"]}
                valueTypographyStyle={typography["Minor-M"]}
                cellWidth={screenWidth * 0.6}
                allowSpaceAfterLeftText={false}
              />
            </SecondContainerLevelTwo>
          </SecondContainerRow>
        </SecondContainer>
        {priceMode === PriceModes.WHITE && showExpandedListView && (
          <SecondContainerLocationRow>
            <SecondContainerLocationFieldRow>
              <VDBText
                color={theme["core-05"]}
                type={typography["Minor-M"]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {getDiamondValue("Location", mItem)}
              </VDBText>
            </SecondContainerLocationFieldRow>

            <SecondContainerLocationFieldRow>
              <VDBText
                color={theme["core-05"]}
                type={typography["Minor-M"]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {getDiamondValue("Region", mItem)}
              </VDBText>
            </SecondContainerLocationFieldRow>
          </SecondContainerLocationRow>
        )}
        {priceMode !== PriceModes.GREEN && (
          <ThirdContainer
            theme={theme}
            viewAlign={
              mItem?.pricing_comment && mItem?.pricing_comment?.length > 0
                ? "space-between"
                : "flex-end"
            }
          >
            {mItem?.pricing_comment && (
              <ThirdIconContainer>
                <Tooltip
                  isOpen={tooltipIsOpen}
                  setIsOpen={setTooltipIsOpen}
                  trigger={() => (
                    <VDBIcon
                      color={
                        theme[productLevelOneColor(ProductCategory.GEMSTONE)]
                      }
                      size={SpaceType.Big}
                      variant={IconsVariant.VDBCustom}
                      name={VDBIcons.PRICE_COMMENT}
                    />
                  )}
                >
                  <VDBText
                    color={theme["core-01"]}
                    type={typography["Minor-R"]}
                  >
                    {mItem?.pricing_comment}
                  </VDBText>
                </Tooltip>
              </ThirdIconContainer>
            )}
            <PriceMainContainer>
              <ThirdContainerCell width={88}>
                <ThirdContainerLabel width={88}>
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    P/ct:
                  </VDBText>
                </ThirdContainerLabel>
                <ThirdContainerValue width={88}>
                  <VDBText
                    color={theme["core-05"]}
                    type={typography["Minor-M"]}
                  >
                    {getDiamondValue("P/ct", mItem, userCurrency)}
                  </VDBText>
                </ThirdContainerValue>
              </ThirdContainerCell>

              <ThirdContainerCell width={88}>
                <ThirdContainerLabel width={88}>
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    Total:
                  </VDBText>
                </ThirdContainerLabel>
                <ThirdContainerValue width={88}>
                  <VDBText
                    color={theme["core-05"]}
                    type={typography["Minor-M"]}
                  >
                    {getDiamondValue("totalPrice", mItem, userCurrency)}
                  </VDBText>
                </ThirdContainerValue>
              </ThirdContainerCell>
            </PriceMainContainer>
          </ThirdContainer>
        )}
        <FourthContainer theme={theme} productTheme={productTheme}>
          <Pressable
            onPress={() => setShowExpandedListView(!showExpandedListView)}
          >
            <VDBIcon
              color={theme[`${productTheme}-06`]}
              name={
                showExpandedListView ? VDBIcons.ARROW_UP : VDBIcons.ARROW_DOWN
              }
              variant={IconsVariant.VDBCustom}
              size={24}
            />
          </Pressable>
        </FourthContainer>
      </ParentContainer>
    </Pressable>
  );
};

const getDiamondValue = (
  key: string,
  item?: Diamond,
  userCurrency?: string
) => {
  switch (key) {
    case "Stock":
      return item?.stock_num ? "VDB-" + item?.stock_num : "-";
    case "Cut":
      return item?.cut_short ? item?.cut_short : "-";
    case "Pol":
      return item?.polish_short ? item?.polish_short : "-";
    case "Sym":
      return item?.symmetry_short ? item?.symmetry_short : "-";
    case "Fluor":
      return item?.fluorescence_intensity_short
        ? item?.fluorescence_intensity_short
        : "-";
    case "Depth":
      return percent(item?.depth_percent);
    case "Table":
      return percent(item?.table_percent);
    case "Crown":
      return getTwoProductProperty(
        item?.crown_height ?? "",
        "%",
        item?.crown_angle ?? "",
        "°"
      );
    case "Pavilion":
      return getTwoProductProperty(
        item?.pavilion_depth ?? "",
        "%",
        item?.pavilion_angle ?? "",
        "°"
      );
    case "Meas":
      return item?.measurement ? item?.measurement : "-";
    case "Ratio":
      return item?.meas_ratio ? item?.meas_ratio : "-";
    case "Girdle":
      return item?.girdle_min_short ? item.girdle_min_short : "-";
    case "Location":
      return item?.locations ? item?.locations[0].location_name : "-";
    case "Region":
      return item?.locations
        ? (item?.locations[0].city ? item?.locations[0].city : "") +
            (item?.locations[0].state ? item?.locations[0].state : "") +
            (item?.locations[0].country
              ? (item?.locations[0].city === "" &&
                item?.locations[0].state === ""
                  ? ""
                  : ", ") + item?.locations[0].country
              : "")
        : "-";
    case "P/ct":
      return item?.price_per_carat
        ? currency(item?.price_per_carat, {
            symbol: userCurrency,
            precision: 0,
          }).format()
        : "-";
    case "discountPercent":
      return percent(item?.discount_percent);
    case "totalPrice":
      return item?.total_sales_price
        ? currency(item?.total_sales_price, {
            symbol: userCurrency,
            precision: 2,
          }).format()
        : "-";
  }
};

const ParentContainer = styled.View<any>`
  height: auto;
  width: auto;
  border-radius: 8px;
  border-color: ${({ theme, isChecked }) =>
    theme[isChecked ? "gem-04" : "core-04"]};
  border-width: ${({ isChecked }) => (isChecked ? 2 : 1)}px;
`;

const FirstContainer = styled.View<any>`
  height: 76px;
  width: auto;
  background-color: ${({ theme }) => theme["core-01"]};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex-direction: row;
  justify-content: space-between;
`;

const FirstContentContainer = styled.View`
  margin-top: 4px;
  margin-start: 4px;
`;

const OptionsContainer = styled.View`
  height: auto;
  width: 24px;
  flex_direction: column;
  justify-content: space-between;
  margin-right: 10px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const ImageAndCheckboxContainer = styled.View<any>`
  height: 76px;
  width: 84px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-top-left-radius: 8px;
  justify-content: center;
  align-items: center;
  padding-left: 8px;
  flex-direction: row;
`;

const ImageAndContentContainer = styled.View<any>`
  flex-direction: row;
`;

const ImageResourceContainer = styled.View`
  padding-left: 2px;
`;

const SecondContainer = styled.View<any>`
  height: auto;
  width: auto;
  background-color: ${({ theme }) => theme["core-02"]};
  padding-horizontal: ${SpaceType.Basic}px;
  border-bottom-left-radius: ${({ priceMode }) =>
    priceMode === PriceModes.GREEN ? 8 : 0}px;
  border-bottom-right-radius: ${({ priceMode }) =>
    priceMode === PriceModes.GREEN ? 8 : 0}px;
`;

const SecondContainerLevelFirst = styled.View<any>`
  height: 24px;
  width: ${({ screenWidth }) => screenWidth}px;
  flex-direction: row;
  align-items: center;
`;

const SecondContainerLevelTwo = styled.View<any>`
  height: 24px;
  width: ${({ screenWidth }) => screenWidth}px;
  flex-direction: row;
  align-items: center;
`;

const SecondContainerRow = styled.View<any>`
  height: 24px;
  width: auto;
  background-color: ${({ theme }) => theme["core-02"]};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SecondContainerLocationRow = styled.View`
  width: auto;
`;

const SecondContainerLocationFieldRow = styled.View`
  padding: ${SpaceType.ExtraSmall}px ${SpaceType.Basic}px;
  align-items: flex-start;
`;

const ThirdContainer = styled.View<any>`
  height: 52px;
  width: auto;
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: row;
  align-items: center;
  justify-content: ${({ viewAlign }) => viewAlign};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const ThirdIconContainer = styled.View`
  height: 24px;
  width: 24px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-start: 20px;
`;

const PriceMainContainer = styled.View`
  height: 52px;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-end: 10px;
`;

const ThirdContainerCell = styled.View<any>`
  flex-direction: column;
  width: ${({ width }) => width}px;
`;

const ThirdContainerLabel = styled.View<any>`
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: ${({ width }) => width}px;
`;

const ThirdContainerValue = styled.View<any>`
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: ${({ width }) => width}px;
`;

const TitleContainer = styled.View<any>`
  min-height: 26px;
  width: ${({ screenWidth }) => screenWidth / 2}px;
  justify-content: flex-end;
`;

const LineView = styled.View<any>`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
  width: auto;
`;

const MenuItemStyle = styled.View`
  border-color: #000000;
  width: 200px;
  padding: 10px;
`;

const AvailabilityDot = styled.View<any>`
  height: 8px;
  width: 8px;
  border-radius: 4px;
  border: 1px;
  border-color: ${({ theme }) => theme["cont-00"]};
  background-color: ${({ bgColor }) => bgColor};
  position: absolute;
  top: 54px;
  left: 36px;
`;

const VideoPlayIconContainer = styled.View<any>`
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${({ theme }) => theme["over-02"]};
  border-radius: 14px;
`;

const FallbackImageContainer = styled.View<any>`
  width: 48px;
  height: 48px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  border-color: ${({ theme }) => theme["core-03"]};
  border-width: 1px;
`;

const VideoBackgroundContainer = styled.View<any>`
  height: 48px;
  width: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const MenuItemContainer = styled.View``;

const FourthContainer = styled.View<{
  productTheme: string;
}>`
  height: 24px;
  width: auto;
  background-color: ${({ theme, productTheme }) => theme[`${productTheme}-03`]};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  align-items: center;
  justify-content: center;
`;

export default GemstoneSearchResultListItem;
